import React from 'react';
import {
  View,
  Text,
  StyleSheet,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { createThemedStyles } from '../theme/useThemedStyles';
import { getGoalTypeProgressColor } from '../theme/goalTypeColors';
import { useGoals, type Goal } from '../context/GoalsContext';
import { useSettings } from '../context/SettingsContext';
import { formatCurrency } from '../utils/currency';
import { formatDateLong } from '../utils/date';
import AdBanner from '../components/AdBanner';
import { useInterstitialAd } from '../hooks/useInterstitialAd';
import WebPage from '../components/web/WebPage';
import PageScroll from '../components/web/PageScroll';
import GoalSearchFilters from '../components/GoalSearchFilters';
import { useGoalFilters } from '../hooks/useGoalFilters';
import { getGoalProgressStatusInfo } from '../utils/goalProgressStatus';

const ProgressScreen = () => {
  const { colors: themeColors } = useTheme();
  const { goals } = useGoals();
  const { settings } = useSettings();

  useInterstitialAd();

  const {
    searchQuery,
    setSearchQuery,
    selectedTypeFilter,
    setSelectedTypeFilter,
    selectedPriorityFilter,
    setSelectedPriorityFilter,
    selectedLoanTypeFilter,
    setSelectedLoanTypeFilter,
    selectedStatusFilter,
    setSelectedStatusFilter,
    showFilters,
    setShowFilters,
    filteredGoals,
    hasActiveFilters,
    clearFilters,
  } = useGoalFilters(goals);
  const styles = useProgressStyles();

  const calculateProjectedAmount = (
    currentAmount: number,
    monthlyContribution: number,
    deadline: string
  ): number => {
    const now = new Date();
    const end = new Date(deadline);
    const monthsRemaining =
      (end.getFullYear() - now.getFullYear()) * 12 + (end.getMonth() - now.getMonth());

    return currentAmount + monthlyContribution * monthsRemaining;
  };

  const renderGoalCard = (goal: Goal) => {
    const progressStatus = getGoalProgressStatusInfo(goal);

    const projectedAmount = calculateProjectedAmount(
      goal.currentAmount,
      goal.monthlyContribution,
      goal.deadline
    );

    const projectedProgress = (projectedAmount / goal.targetAmount) * 100;
    const isComplete = goal.currentAmount >= goal.targetAmount;
    const progressColor = getGoalTypeProgressColor(goal.type, themeColors);

    return (
      <View key={goal.id} style={styles.goalCard}>
        <View style={styles.header}>
          <View style={styles.goalInfo}>
            <Text style={styles.goalName}>{goal.name}</Text>
            <Text style={styles.goalType}>{goal.type}</Text>
          </View>
          <View style={[styles.statusBadge, { backgroundColor: progressStatus.color }]}>
            <Text style={styles.statusText}>{progressStatus.status}</Text>
          </View>
        </View>

        <View style={styles.progressSection}>
          <View style={styles.progressBar}>
            <View
              style={[
                styles.progressFill,
                {
                  width: `${(goal.currentAmount / goal.targetAmount) * 100}%`,
                  backgroundColor: progressColor,
                },
              ]}
            />
            {!isComplete ? (
              <View
                style={[
                  styles.projectedFill,
                  {
                    left: `${(goal.currentAmount / goal.targetAmount) * 100}%`,
                    width: `${((projectedAmount - goal.currentAmount) / goal.targetAmount) * 100}%`,
                    backgroundColor: progressColor,
                  },
                ]}
              />
            ) : null}
          </View>
          <View style={styles.amountRow}>
            <Text style={styles.currentAmount}>
              {formatCurrency(goal.currentAmount, settings.currency.code, settings.currency.symbol)}
            </Text>
            <Text style={styles.targetAmount}>
              {formatCurrency(goal.targetAmount, settings.currency.code, settings.currency.symbol)}
            </Text>
          </View>
        </View>

        <View style={styles.detailsSection}>
          <Text style={[styles.statusMessage, isComplete && styles.completionMessage]}>
            {progressStatus.message}
          </Text>

          {isComplete && goal.completedDate ? (
            <Text style={styles.completedDate}>
              Completed on {formatDateLong(goal.completedDate, settings.dateFormat)}
            </Text>
          ) : null}

          {!isComplete ? (
            <>
              <View style={styles.projectionRow}>
                <Text style={styles.projectionLabel}>Projected by deadline:</Text>
                <Text
                  style={[
                    styles.projectionAmount,
                    {
                      color:
                        projectedProgress >= 100 ? themeColors.success.main : themeColors.error.main,
                    },
                  ]}
                >
                  {formatCurrency(projectedAmount, settings.currency.code, settings.currency.symbol)}
                  ({projectedProgress.toFixed(2)}%)
                </Text>
              </View>
              <View style={styles.contributionRow}>
                <Text style={styles.contributionLabel}>Monthly contribution:</Text>
                <Text style={styles.contributionAmount}>
                  {formatCurrency(
                    goal.monthlyContribution,
                    settings.currency.code,
                    settings.currency.symbol
                  )}
                </Text>
              </View>
            </>
          ) : null}
        </View>
      </View>
    );
  };

  return (
    <WebPage title="Progress">
      <PageScroll style={styles.container}>
        <GoalSearchFilters
          goals={goals}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          selectedTypeFilter={selectedTypeFilter}
          onTypeFilterChange={setSelectedTypeFilter}
          selectedPriorityFilter={selectedPriorityFilter}
          onPriorityFilterChange={setSelectedPriorityFilter}
        selectedLoanTypeFilter={selectedLoanTypeFilter}
        onLoanTypeFilterChange={setSelectedLoanTypeFilter}
        selectedStatusFilter={selectedStatusFilter}
        onStatusFilterChange={setSelectedStatusFilter}
        showFilters={showFilters}
          onToggleFilters={() => setShowFilters(!showFilters)}
          onClearFilters={clearFilters}
        />

        {filteredGoals.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {hasActiveFilters
                ? 'No goals match your filters'
                : 'No goals yet. Add your first goal from My Goals!'}
            </Text>
          </View>
        ) : (
          <View style={styles.cardsContainer}>{filteredGoals.map(renderGoalCard)}</View>
        )}
      </PageScroll>

      <AdBanner />
    </WebPage>
  );
};

const useProgressStyles = createThemedStyles((colors) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background.goals,
  },
  cardsContainer: {
    padding: 16,
    paddingTop: 0,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 16,
    color: colors.text.secondary,
    textAlign: 'center',
  },
  goalCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  goalInfo: {
    flex: 1,
    flexDirection: 'column',
  },
  goalName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.text.primary,
    flex: 1,
    marginRight: 16,
  },
  goalType: {
    fontSize: 14,
    color: colors.text.secondary,
    marginTop: 4,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    width: 100,
    alignItems: 'center',
  },
  statusText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 12,
    textAlign: 'center',
  },
  progressSection: {
    marginBottom: 16,
  },
  progressBar: {
    height: 12,
    backgroundColor: colors.grey[200],
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
  },
  projectedFill: {
    position: 'absolute',
    top: 0,
    height: '100%',
    opacity: 0.45,
  },
  amountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  currentAmount: {
    fontSize: 14,
    color: colors.text.primary,
  },
  targetAmount: {
    fontSize: 14,
    color: colors.text.secondary,
  },
  detailsSection: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingTop: 16,
  },
  statusMessage: {
    fontSize: 14,
    color: colors.text.primary,
    marginBottom: 12,
  },
  projectionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  projectionLabel: {
    fontSize: 14,
    color: colors.text.secondary,
  },
  projectionAmount: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  contributionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  contributionLabel: {
    fontSize: 14,
    color: colors.text.secondary,
  },
  contributionAmount: {
    fontSize: 14,
    color: colors.text.primary,
    fontWeight: 'bold',
  },
  completionMessage: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.success.main,
    textAlign: 'center',
    marginVertical: 16,
  },
  completedDate: {
    fontSize: 14,
    color: colors.text.secondary,
    textAlign: 'center',
    marginBottom: 16,
    fontStyle: 'italic',
  },
}));

export default ProgressScreen;
